import _ from 'lodash'
import {createSelector} from 'reselect'
import {getFormattedTourDate, getFormattedTourDateTime} from 'manager/util/helper'
import {differenceInDays, isBefore, isValid, parse, startOfToday} from 'date-fns'
import {
  ANSWER_TYPE,
  PROPERTY_TOUR_SCHEDULE_STATUS,
  QUESTION_TYPE,
  RELEASE_SPEEDBUMP_CUTOFF_DAYS,
} from 'common/constants'
import {getHumanReadableLeadAvailability} from 'common/util/helper'

const TOUR_STATUS_TO_CONFIRM = [
  PROPERTY_TOUR_SCHEDULE_STATUS.REQUESTED,
  PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED,
  PROPERTY_TOUR_SCHEDULE_STATUS.SUGGESTED,
]

export const selectReferral = state => state.referral.referral || {}

export const selectReferralRemovingNA = createSelector(selectReferral, referral =>
  Object.keys(referral).reduce((acc, curr) => {
    const value = referral[curr]

    return {
      ...acc,
      [curr]: value === "N/A" ? null : value
    }
  }, {})
)

export const selectReferralName = createSelector(selectReferral, referral => referral.name)

export const selectReferralQualiaEnabled = createSelector(selectReferral, referral => referral.qualia_title_enabled)

export const selectReferralLastUpdated = createSelector(selectReferral, referral => referral.agent_last_update_at)

export const selectOfferStatus = createSelector(selectReferral, referral => referral.agent_offer_status)

export const selectTransactionType = createSelector(selectReferral, referral => referral.transaction_type)

export const selectReferralQualiaAskAgentQuestion = createSelector(
  selectReferral,
  selectTransactionType,
  (referral, transactionType) => referral.qualia_ask_agent_title_question && transactionType !== 'lease')

export const selectIsLease = createSelector(selectReferral, referral => referral.is_lease)

export const selectAgentStatus = createSelector(selectReferral, referral => referral.agent_status)

export const selectAgentContractMLS = createSelector(selectReferral, referral => referral.agent_contract_mls)

export const selectAgentContractPropertyAddress = createSelector(selectReferral, referral => referral.agent_contract_property_address)

export const selectAgentContractAmount = createSelector(selectReferral, referral => referral.agent_contract_amount)

export const selectAgentContractCloseDate = createSelector(selectReferral, referral => referral.agent_contract_close_date)

export const selectAssignmentHash = createSelector(selectReferral, referral => referral.assignment_hash)

export const selectAssignmentTransactionHash = createSelector(selectReferral, referral => referral.hash)

export const selectAgentCloseMLS = createSelector(selectReferral, referral => referral.agent_close_mls)

export const selectAgentClosePropertyAddress = createSelector(selectReferral, referral => referral.agent_close_property_address)

export const selectAgentCloseAmount = createSelector(selectReferral, referral => referral.agent_close_amount)

export const selectAgentCloseDate = createSelector(selectReferral, referral => referral.agent_close_date)

export const selectAgentCloseAgentCommission = createSelector(selectReferral, referral => referral.agent_reported_close_agent_commission)

export const selectIsAgentsFirstContract = createSelector(selectReferral, referral => referral.is_agents_first_contract_referral)

export const selectAgentIsReferralOnHold = createSelector(selectReferral, referral => referral.is_holding)

export const selectPropertyInquiryData = createSelector(
  selectReferral,
  referral => _.get(referral, ['property_inquiries', 0]) || {}
)

export const selectReferralCreatedAt = createSelector(selectReferral, referral => referral.created_at)

export const selectPropertyInquiryRequestedTourDate = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.requested_date)

export const selectPropertyInquiryRequestedTourTimeframe = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.requested_timeframe)

export const selectPropertyInquiryRequestedTourScheduledDate = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.scheduled_at)

export const selectPropertyInquiryRequestedTourStatus = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.tour_status)

export const selectIsPropertyTourStatusPresent = createSelector(selectPropertyInquiryRequestedTourStatus, status => !!status)

export const selectPropertyInquiries = createSelector(selectReferral, referral => referral.property_inquiries || [])

export const selectPropertyInquiryInfo = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.property)

export const selectPropertyInquiryAddress = createSelector(selectPropertyInquiryInfo, propertyInfo => propertyInfo.street_address || '')

export const selectPropertyInquiryCity = createSelector(selectPropertyInquiryInfo, propertyInfo => propertyInfo.city || '')

export const selectPropertyInquiryState = createSelector(selectPropertyInquiryInfo, propertyInfo => propertyInfo.state || '')

export const selectPropertyInquiryZip = createSelector(selectPropertyInquiryInfo, propertyInfo => propertyInfo.zip || '')


export const selectPropertyInquiryLocation = createSelector(selectPropertyInquiryAddress, selectPropertyInquiryCity, selectPropertyInquiryState, selectPropertyInquiryZip, (address, city, state, zip) => {
    return {
      address,
      city,
      state,
      zip
    }
  })

export const selectPropertyInquiryMLSNumber = createSelector(selectPropertyInquiryInfo, propertyInfo => propertyInfo.mls_number)

export const selectPropertyInquiryImage = createSelector(selectPropertyInquiryInfo, propertyInfo => propertyInfo.image)

export const selectPropertyInquiryTourOpId = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.tour_op_id)

export const selectPropertyInquiryRDCLink = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.rdc_link)

export const selectIsPropertyRDCLinkPresent = createSelector(selectPropertyInquiryRDCLink, rdc_link => !!rdc_link)

export const selectIsPropertyInquiryRDCLead = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.is_rdc_lead)

export const selectLeadAvailabilityData = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.lead_availability)

export const selectIsLeadAvailabilityVisible = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.lead_availability && !inquiryData.lead_availability.date)

export const selectReadableLeadAvailability = createSelector(selectLeadAvailabilityData, selectIsLeadAvailabilityVisible, (leadAvailability, isVisible) => isVisible ? getHumanReadableLeadAvailability(leadAvailability) : '')

export const selectIsPropertyTourVirtual = createSelector(selectPropertyInquiryData, inquiryData => inquiryData.is_virtual)

export const selectIsPropertyInquiryRequestedTourDateBeforeToday = createSelector(selectPropertyInquiryRequestedTourDate, date => {
  const requestedDateParsed = parse(date)
  return isBefore(requestedDateParsed, startOfToday())
})

export const selectPropertyInquiryToursWithNeededUpdates = createSelector(
  selectPropertyInquiries,
  propertyInquiries => {
    return propertyInquiries.filter(({ tour_status, requested_date, scheduled_at }) => {
      if (!TOUR_STATUS_TO_CONFIRM.includes(tour_status)) {
        return false
      }

      const parsedTourDate = parse(requested_date || scheduled_at)

      return isValid(parsedTourDate) && isBefore(parsedTourDate, new Date())
    })
  }
)

export const selectIsPropertyInquiryTourUpdateNeeded = createSelector(
  selectPropertyInquiryToursWithNeededUpdates,
  (toursNeedingUpdates = []) => toursNeedingUpdates.length > 0
)

export const selectPropertyInquiryTourDateHidden = createSelector(selectPropertyInquiryRequestedTourStatus, selectIsPropertyInquiryRequestedTourDateBeforeToday, (status, requestDateEarlierThanToday) => {
  if (!status) {
    return true
  }

  if (status == PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED || status == PROPERTY_TOUR_SCHEDULE_STATUS.COMPLETED) {
    return false
  }

  if (status == PROPERTY_TOUR_SCHEDULE_STATUS.UNKNOWN || status == PROPERTY_TOUR_SCHEDULE_STATUS.CANCELLED) {
    return true
  }

  return requestDateEarlierThanToday
})

export const selectIsPropertyInquiryTourConfirmable = createSelector(selectPropertyInquiryRequestedTourStatus, selectIsPropertyInquiryRequestedTourDateBeforeToday, selectAgentIsReferralOnHold, selectIsPropertyTourVirtual, (status, requestDateEarlierThanToday, isHolding, isVirtual) => {
  if (!status || isHolding || isVirtual) {
    return false
  }

  if (status != PROPERTY_TOUR_SCHEDULE_STATUS.REQUESTED) {
    return false
  }

  return !requestDateEarlierThanToday
})

export const selectIsPropertyInquiryTourRescheduleable = createSelector(selectPropertyInquiryRequestedTourStatus, selectIsPropertyInquiryRequestedTourDateBeforeToday, selectAgentIsReferralOnHold, selectIsPropertyTourVirtual, (status, requestDateEarlierThanToday, isHolding, isVirtual) => {
  if (!status || isHolding || isVirtual) {
    return false
  }

  if (status != PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED) {
    return false
  }

  return !requestDateEarlierThanToday
})

export const selectIsPropertyInquiryTourSuggestable = createSelector(selectReferral, referral => referral.is_tour_suggestible)

export const selectPropertyInquiryTitle = createSelector(selectPropertyInquiryRequestedTourStatus, selectIsPropertyTourVirtual, (status, isVirtual) => {
  if (!status || PROPERTY_TOUR_SCHEDULE_STATUS.SUGGESTED === status) {
    return 'Property inquiry'
  }

  const tourType = isVirtual ? 'Video' : 'Property'

  return [
    PROPERTY_TOUR_SCHEDULE_STATUS.REQUESTED,
    PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED,
    PROPERTY_TOUR_SCHEDULE_STATUS.COMPLETED,
  ].includes(status) ? `${tourType} tour ${status}` : `${tourType} tour requested`
})

export const selectFormattedPropertyInquiryTourDateTime = createSelector(selectPropertyInquiryRequestedTourStatus, selectPropertyInquiryRequestedTourDate, selectPropertyInquiryRequestedTourScheduledDate, selectPropertyInquiryRequestedTourTimeframe, (status, requestedDate, scheduledDate, requestedTimeframe) => {
    return getFormattedTourDateTime(status !== PROPERTY_TOUR_SCHEDULE_STATUS.REQUESTED, requestedDate, requestedTimeframe, scheduledDate)
})

export const selectFormattedPropertyInquiryTourDate = createSelector(selectPropertyInquiryRequestedTourStatus, selectPropertyInquiryRequestedTourDate, selectPropertyInquiryRequestedTourScheduledDate, (status, requestedDate, scheduledDate) => {
    return getFormattedTourDate(status !== PROPERTY_TOUR_SCHEDULE_STATUS.REQUESTED, requestedDate, scheduledDate)
})

export const selectShouldShowFirstContractSuccess = createSelector(
  selectIsAgentsFirstContract,
  isAgentsFirstContract => isAgentsFirstContract
)

export const selectIsReferralRecent = createSelector(
  selectReferralCreatedAt,
  createdAt => differenceInDays(new Date(), createdAt) < RELEASE_SPEEDBUMP_CUTOFF_DAYS
)

export const selectReferralTags = createSelector(
  selectReferral,
  referral => referral.transaction_tags || []
)

export const selectReferralIsTransition = createSelector(
  selectReferralTags,
  tags => tags.some(tag => tag.type === 'transition')
)

export const selectReferralIsPlatinum = createSelector(
  selectReferralTags,
  tags => tags.some(tag => tag.type === 'platinum')
)

export const selectReferralIsPromotional = createSelector(
  selectReferralTags,
  tags => tags.some(tag => tag.type === 'promotional')
)

export const selectReferralIsCashback = createSelector(
  selectReferral,
  referral => !!referral.is_cashback
)

export const selectReferralStatusIsInvalid = createSelector(
  selectReferral,
  referral => !!referral.is_status_invalid
)

export const selectSelectedSettlementProvider = createSelector(
  selectReferral,
    referral => referral.selected_settlement_provider
)

export const selectSelectedSettlementProviderName = createSelector(
  selectSelectedSettlementProvider,
    selectedProvider => selectedProvider ? selectedProvider.settlement_provider_name : null
)

export const selectSelectedProviderIsQualiaSupported = createSelector(
  selectSelectedSettlementProvider,
    selectedProvider => selectedProvider ? selectedProvider.is_qualia_supported : null
)

export const selectSelectedProviderAddress = createSelector(
  selectSelectedSettlementProvider,
    selectedProvider => selectedProvider ? selectedProvider.settlement_provider_flat_address : null
)

export const selectProviderReferralSent = createSelector(
  selectSelectedSettlementProvider,
    selectedProvider => selectedProvider ? selectedProvider.referral_sent : null
)

export const selectLatestQualiaOrder = createSelector(
  selectSelectedSettlementProvider,
    selectedProvider => selectedProvider ? selectedProvider.qualia_order : null
)

export const selectInviteProviderError = state => state.referral.inviteProviderError

export const selectPlaceQualiaOrderError = state => state.referral.placeQualiaOrderError

export const selectTransactionOpId = createSelector(selectReferral, referral => referral.transaction_op_id)

export const selectAgentOpId = createSelector(selectReferral, referral => referral.agent_op_id)

export const selectTitleWillYouSelectQuestion = createSelector(selectReferral, referral => referral.title_questions && referral.title_questions[QUESTION_TYPE.TITLE_WILL_YOU_SELECT] || null)
export const selectIsTitleWillYouSelectQuestionAnsweredYes = createSelector(
  selectTitleWillYouSelectQuestion,
  (titleWillYouSelectQuestion) => titleWillYouSelectQuestion && titleWillYouSelectQuestion[ANSWER_TYPE.YES_NO] === 'yes'
)
export const selectTitleDoYouKnowProviderQuestion = createSelector(selectReferral, referral => referral.title_questions && referral.title_questions[QUESTION_TYPE.TITLE_DO_YOU_KNOW_PROVIDER] || null)
export const selectTitleWhoIsProviderQuestion = createSelector(selectReferral, referral => referral.title_questions && referral.title_questions[QUESTION_TYPE.TITLE_WHO_IS_PROVIDER] || null)
export const selectTitleWhoIsProviderName = createSelector(
  selectTitleWhoIsProviderQuestion,
  (titleWhoIsProviderQuestion) =>
    (titleWhoIsProviderQuestion && titleWhoIsProviderQuestion.title_selection)
      ? titleWhoIsProviderQuestion.title_selection.name
      : ""
)
export const selectTitleProviderIsQualia = createSelector(
  selectTitleWhoIsProviderQuestion,
  (titleWhoIsProviderQuestion) =>
      titleWhoIsProviderQuestion && titleWhoIsProviderQuestion.title_selection && titleWhoIsProviderQuestion.title_selection.id
)
export const selectTitlePlacedOrderQuestion = createSelector(selectReferral, referral => referral.title_questions && referral.title_questions[QUESTION_TYPE.TITLE_PLACED_ORDER] || null)
export const selectIsTitlePlacedOrderQuestionAnsweredNo = createSelector(
  selectIsTitleWillYouSelectQuestionAnsweredYes,
  selectTitleProviderIsQualia,
  selectTitlePlacedOrderQuestion,
  (isTitleWillYouSelectQuestionAnsweredYes, titleProviderIsQualia, titlePlacedOrderQuestion) =>
    isTitleWillYouSelectQuestionAnsweredYes && titleProviderIsQualia && titlePlacedOrderQuestion && titlePlacedOrderQuestion[ANSWER_TYPE.YES_NO] === 'no'
)
export const selectTitlePlaceOrderNowQuestion = createSelector(selectReferral, referral => referral.title_questions && referral.title_questions[QUESTION_TYPE.TITLE_PLACE_ORDER_NOW] || null)
export const selectIsTitlePlaceOrderNowAnsweredYes = createSelector(
  selectIsTitlePlacedOrderQuestionAnsweredNo,
  selectTitleProviderIsQualia,
  selectTitlePlaceOrderNowQuestion,
  (isTitlePlacedOrderQuestionAnsweredNo, titleProviderIsQualia, titlePlaceOrderNowQuestion) =>
    isTitlePlacedOrderQuestionAnsweredNo && titleProviderIsQualia && titlePlaceOrderNowQuestion && titlePlaceOrderNowQuestion[ANSWER_TYPE.YES_NO] === 'yes'
)
